import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Tooltip,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme, alpha } from "@mui/material/styles";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GroupIcon from "@mui/icons-material/Group";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function WorkplaceTab({ userDetails }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Grid container spacing={{ xs: 3, md: 4 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BusinessIcon />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {t("userProfile.organizationInfo", "Organization")}
            </Typography>
          </Box>

          <List sx={{ py: 0 }}>
            <ListItem sx={{ px: 0, py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <WorkIcon color="action" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={t("userProfile.departmentLabel")}
                secondary={
                  userDetails?.department?.name ||
                  t("userProfile.noDepartmentText")
                }
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                  fontWeight: 500,
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                  fontWeight: 600,
                  mt: 0.5,
                }}
              />
            </ListItem>

            <Divider />

            <ListItem sx={{ px: 0, py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AdminPanelSettingsIcon color="action" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={t("userProfile.managerLabel")}
                secondary={
                  userDetails?.manager
                    ? `${userDetails.manager.first_name} ${userDetails.manager.last_name}`
                    : t("userProfile.noManagerText")
                }
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                  fontWeight: 500,
                }}
                secondaryTypographyProps={{
                  variant: "body1",
                  color: "text.primary",
                  fontWeight: 600,
                  mt: 0.5,
                }}
              />
            </ListItem>
          </List>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Stack spacing={3} sx={{ height: "100%" }}>
          {/* User Groups */}
          <Paper
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              flex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GroupIcon />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                {t("userProfile.userGroupsTitle")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {userDetails?.user_groups?.length > 0 ? (
                userDetails.user_groups.map((group) => (
                  <Tooltip key={group.id} title={group.name}>
                    <Chip
                      variant="outlined"
                      color="primary"
                      label={group.name}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    />
                  </Tooltip>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  {t("userProfile.noGroupsAssigned", "No groups assigned")}
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Sites */}
          <Paper
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              flex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: "secondary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocationOnIcon />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                {t("userProfile.sitesTitle")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {userDetails?.sites?.length > 0 ? (
                userDetails.sites.map((site) => (
                  <Tooltip key={site.id} title={site.name}>
                    <Chip
                      variant="outlined"
                      color="secondary"
                      label={site.name}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        },
                      }}
                    />
                  </Tooltip>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  {t("userProfile.noSitesAssigned", "No sites assigned")}
                </Typography>
              )}
            </Box>
          </Paper>
        </Stack>
      </Grid>
    </Grid>
  );
}

WorkplaceTab.propTypes = {
  userDetails: PropTypes.object,
};

export default WorkplaceTab;
